import { gql, useMutation } from '@apollo/client';
import { useFormikContext } from 'formik';
import { Set } from 'immutable';
import { keyBy } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router';
import { FormGroup, Label } from 'reactstrap';
import styled from 'styled-components';
import { timeColumns } from '../../utils/columns';
import { asCheck, asQuantityWithUnit, asWarning } from '../../utils/formatters';
import { getFirstQueryResult } from '../../utils/graphql';
import {
  useDataScope,
  useQueryWithFarmIdAndMsg,
  useUserRole,
} from '../../utils/hooks';
import { metricsQueryString } from '../../utils/utils';
import {
  SidebarEditContext,
  SidebarEditContextProvider,
} from '../../contexts/SidebarEditContext';
import {
  applyBulkEditsToObject,
  isAsterisk,
  mergeObjects,
} from '../../utils/bulkEdits';
import { ShrinkContent, ShrinkContentColumn } from '../ShrinkContent';
import { useRenderWithBulkErrors } from '../../hooks/useRenderWithBulkErrors';
import LineItemsTable from '../tables/LineItemsTable';
import { searchBy } from '../../utils/search';
import {
  DeleteCapexesMutation,
  DeleteCapexTemplatesMutation,
  UpdateCapexesMutation,
  UpdateCapexTemplatesMutation,
} from '../../mutations';
import { bulkCapexSchema, bulkEditFields } from '../../constants/capexes';
import SidebarForm from './SidebarForm';
import {
  AssignmentInput,
  PurchaseYearInput,
  QuantityInput,
  UnitCostInput,
} from './capex-form-components';
import MaxWidthDiv from '../basics/MaxWidthDiv';
import { SmallFontLargeFormattedInput } from './template-components';
import { captureEvent } from '../../utils/analytics';
import { MULTIPLE } from '../../constants/fields';
import useMutateWithState from '../../hooks/useMutateWithState';

const TableTitle = styled.h1`
  margin: var(--header-margin-y) var(--cell-padding) 0;
`;

const COLS = [
  {
    accessor: 'name',
    displayString: 'Name',
  },
  ...timeColumns,
];

const SUPERADMIN_COLS = [
  ...COLS,
  {
    accessor: 'valid',
    displayString: 'Valid',
    format: asWarning,
    style: { verticalAlign: 'middle' },
    isSmall: true,
  },
];

const FARM_ONLY_COL = {
  accessor: 'purchased',
  displayString: 'Purchased',
  format: asCheck,
  style: { verticalAlign: 'middle' },
  isSmall: true,
};

// this is separate from FARM_ONLY_COL
// because its accessor and formatter looks at several values from the row
// which metricsQueryString cannot handle currently
const FARM_CROPS_COL = {
  accessor: (row) => row,
  displayString: 'Proration',
  format: ({ allCrops, purchased }) => {
    const filteredCrops = allCrops.filter((crop) => crop.referencesCapex);
    if (purchased && filteredCrops.length === 0) {
      return 'All crops';
    }
    if (!purchased && filteredCrops.length === 0) {
      return 'No crops';
    }
    if (filteredCrops.length === 1) {
      return filteredCrops[0].crop.name;
    }
    return `${filteredCrops[0].crop.name} +${filteredCrops.length - 1} more`;
  },
};

const TEMPLATE_CROPS_COL = {
  accessor: 'allCrops',
  displayString: 'Proration',
  format: (allCrops) => {
    const filteredCrops = allCrops.filter((crop) => {
      return crop.referencesCapex;
    });
    if (filteredCrops.length === 0) {
      return 'No crops';
    }
    if (filteredCrops.length === 1) {
      return filteredCrops[0].crop.name;
    }
    return `${filteredCrops[0].crop.name} +${filteredCrops.length - 1} more`;
  },
};

const transformBulkEdits = ({ capexes, ...values }) => {
  if (!capexes) return { ...values };
  return {
    ...values,
    capexes: capexes.map((capex) =>
      applyBulkEditsToObject(values, capex, bulkEditFields),
    ),
  };
};

const capexesSchema = yup
  .object()
  // Apply bulk edits to each selected capex
  .transform(transformBulkEdits)
  .shape({
    capexes: yup.array().of(bulkCapexSchema).nullable(),
  });

// use SUPERADMIN_COLS in all of these to fetch validity
// but will only show validity to superadmins
export const CapexResourcesQuery = gql`
  query CapexResources {
    capexResources {
      id
      ${bulkEditFields.join(' ')}
      ${metricsQueryString(SUPERADMIN_COLS)}
      allCrops {
        crop {
          id
          name
        }
        referencesCapex
      }
    }
  }
`;

const TeamCapexResourcesQuery = gql`
  query TeamCapexResourcesQuery($id: ID!) {
    team(id: $id) {
      id
      capexResources {
        id
        ${bulkEditFields.join(' ')}
        ${metricsQueryString(SUPERADMIN_COLS)}
        allCrops {
          crop {
            id
            name
          }
          referencesCapex
        }
      }
    }
  }
`;

export const RawCapexesQuery = gql`
  query RawCapexes($farmId: ID!) {
    rawCapexes(farmId: $farmId) {
      id
      ${metricsQueryString([FARM_ONLY_COL, ...SUPERADMIN_COLS])}
      ${bulkEditFields.join(' ')}
      allCrops {
        crop {
          id
          name
        }
        referencesCapex
      }
    }
  }
`;

const multipleOrPlaceholder = (value, placeholder) =>
  isAsterisk(value) ? MULTIPLE : placeholder;

const CapexesForm = ({ itemCount, isTemplate = false }) => {
  const { values, dirty, isValid } = useFormikContext();
  const { isSuperadmin } = useUserRole();

  const { setIsDirty, setHasValidationError } = useContext(SidebarEditContext);

  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHasValidationError(!isValid);
  }, [isValid]); // eslint-disable-line react-hooks/exhaustive-deps

  const { renderWithBulkErrors } = useRenderWithBulkErrors('capexes');
  return (
    <>
      <FormGroup className="d-inline-block">
        <div className="font-weight-bold">{`${asQuantityWithUnit(
          'CapExes',
          'CapEx',
        )(itemCount)} selected for editing`}</div>
      </FormGroup>
      <div className="d-flex">
        <UnitCostInput
          currency={!isAsterisk(values.unitCost)}
          placeholder={multipleOrPlaceholder(values.unitCost, 'Unit Cost')}
        >
          {renderWithBulkErrors('unitCost')}
        </UnitCostInput>
        <QuantityInput
          placeholder={multipleOrPlaceholder(values.quantity, 'Quantity')}
        >
          {renderWithBulkErrors('quantity')}
        </QuantityInput>
        <PurchaseYearInput
          placeholder={multipleOrPlaceholder(
            values.purchaseYear,
            'Purchase Year',
          )}
        >
          {renderWithBulkErrors('purchaseYear')}
        </PurchaseYearInput>
        <AssignmentInput
          placeholder={multipleOrPlaceholder(values.assignment, undefined)}
          isSuperadmin={isSuperadmin}
        />
        {!isTemplate && (
          <FormGroup>
            <Label>Purchased?</Label>
            <MaxWidthDiv maxWidth="80px">
              <SmallFontLargeFormattedInput
                type="text"
                value={
                  isAsterisk(values.purchased)
                    ? MULTIPLE
                    : values.purchased
                    ? 'Yes'
                    : 'No'
                }
                bsSize="sm"
                disabled
              />
            </MaxWidthDiv>
          </FormGroup>
        )}
      </div>
    </>
  );
};

const CapexesTable = ({
  query,
  queryOptions,
  getQueryResult = getFirstQueryResult,
  showFarmColumns = false,
  onSubmit,
  onDeleteWithVars,
  status,
}) => {
  const { farmId, teamId } = useParams();
  // For tracking
  const scope = useDataScope();
  const [selectedItems, setSelectedItems] = useState(new Set());

  const { msg, data } = useQueryWithFarmIdAndMsg(
    query,
    {
      fetchPolicy: 'cache-and-network',
      ...queryOptions,
      includeFarmId: showFarmColumns,
    },
    true,
  );
  const navigate = useNavigate();
  const getItems = (itemData) => {
    const items = getQueryResult(itemData);
    return items.map((item) => ({
      ...item,
      onClick: () => navigate(`${item.id}/edit`),
    }));
  };
  const items = keyBy(getQueryResult(data), 'id');

  const { isSuperadmin } = useUserRole();
  let cols = isSuperadmin ? SUPERADMIN_COLS : COLS;
  if (showFarmColumns) {
    cols = [...cols, FARM_ONLY_COL, FARM_CROPS_COL];
  } else {
    cols = [...cols, TEMPLATE_CROPS_COL];
  }

  const { blockIfDirty } = useContext(SidebarEditContext);

  const transformBulkCapexesForForm = useCallback(() => {
    // Return null unless at least one capex is selected
    if (!selectedItems?.size) return null;
    const capexes = [...selectedItems]
      .map((capexId) => items[capexId])
      .filter((capex) => capex); // Remove null capexes resulting from invalid capexIds
    return {
      // Include purchased here for display, but not editing
      ...mergeObjects(capexes, [...bulkEditFields, 'purchased']),
      capexes,
    };
  }, [items, selectedItems]);

  const transformedEditing = useMemo(() => {
    return transformBulkCapexesForForm();
  }, [transformBulkCapexesForForm]);

  const selectedCapexes = [...selectedItems]
    .map((capexId) => items[capexId])
    .filter((capex) => capex); // Remove null capexes resulting from invalid capexIds

  return (
    <ShrinkContentColumn>
      <TableTitle>Equipment & Infrastructure</TableTitle>
      <ShrinkContent className="d-flex no-wrap position-relative">
        <LineItemsTable
          className="overflow-hidden"
          data={data}
          msg={msg}
          cols={cols}
          getItems={getItems}
          createButton="New CapEx"
          createButtonOnClick={() => navigate('new')}
          hover
          searchable
          showCount
          search={searchBy(['name'])}
          selectedIds={selectedItems && selectedItems.toArray()}
          selectedItems={selectedItems}
          setSelectedItems={(selected) => {
            setSelectedItems(selected);
            captureEvent('multiple-capexes-selected', {
              count: selected.size,
              scope,
            });
          }}
          // Note that purchased only applies to farm capexes
          sortColumn={['purchased', 'name']}
          sortDirection={['desc', 'asc']}
        />
        {selectedItems.size > 0 && (
          <SidebarForm
            editing={transformedEditing}
            onSubmit={onSubmit(capexesSchema)}
            onDelete={() => {
              captureEvent('multiple-capexes-deleted', {
                count: selectedCapexes.length,
                scope,
              });
              return onDeleteWithVars({
                id: undefined,
                updatedAt: undefined,
                farmId,
                teamId,
                capexes: selectedCapexes.map((item) => ({
                  id: item.id,
                  updatedAt: item.updatedAt,
                })),
              })();
            }}
            onClose={() => setSelectedItems(new Set())}
            onCancel={() => {
              blockIfDirty(() => setSelectedItems(new Set()));
            }}
            loading={false}
            validationSchema={capexesSchema}
            showDelete
            status={status}
            deleteButtonText={`Delete ${asQuantityWithUnit(
              'CapExes',
              'CapEx',
            )(selectedItems.size)}`}
            confirmEditText="Your capexes have unsaved changes."
            validationErrorText="Your capexes have validation errors that need to be addressed."
          >
            <CapexesForm
              editing={transformedEditing}
              itemCount={selectedItems.size}
              isTemplate={!showFarmColumns}
            />
          </SidebarForm>
        )}
      </ShrinkContent>
    </ShrinkContentColumn>
  );
};

const transformCapexValues = ({ teamId, farmId }) => (values) => {
  return {
    teamId,
    farmId,
    capexes: values.capexes.map(
      ({ id, updatedAt, assignment, purchaseYear, quantity, unitCost }) => ({
        id,
        updatedAt,
        assignment,
        purchaseYear,
        quantity,
        unitCost,
      }),
    ),
  };
};

const useMutations = ({
  submitMutation,
  deleteMutation,
  fetchAllRecordsQuery,
  transformValues,
}) => {
  const { teamId, farmId } = useParams();
  const [status, mutateAndSetState] = useMutateWithState();
  const [mutate] = useMutation(submitMutation);
  const [deleteResource] = useMutation(deleteMutation, {
    refetchQueries: [
      { query: fetchAllRecordsQuery, variables: { id: teamId, farmId } },
    ],
    awaitRefetchQueries: true,
  });

  const onSubmit = (schema) => async (values, { setSubmitting }) => {
    try {
      const input = transformValues(schema.cast(values));
      return await mutateAndSetState(() =>
        mutate({
          variables: { input },
          refetchQueries: [
            { query: fetchAllRecordsQuery, variables: { farmId, id: teamId } },
          ],
          awaitRefetchQueries: true,
        }),
      ).then((response) => {
        setSubmitting(false);
        return response;
      });
    } catch (e) {
      console.error('Casting error:', e.message);
      return Promise.reject(e);
    }
  };
  const onDeleteWithVars = (vars) => () => {
    return mutateAndSetState(() =>
      deleteResource({
        variables: { input: vars },
      }),
    );
  };

  return { onSubmit, onDeleteWithVars, status };
};

export const CapexResourcesTable = () => {
  const props = useMutations({
    submitMutation: UpdateCapexTemplatesMutation,
    deleteMutation: DeleteCapexTemplatesMutation,
    fetchAllRecordsQuery: CapexResourcesQuery,
    transformValues: transformCapexValues({}),
  });

  return (
    <SidebarEditContextProvider>
      <CapexesTable query={CapexResourcesQuery} {...props} />
    </SidebarEditContextProvider>
  );
};

export const TeamCapexResourcesTable = ({ teamId }) => {
  const props = useMutations({
    submitMutation: UpdateCapexTemplatesMutation,
    deleteMutation: DeleteCapexTemplatesMutation,
    fetchAllRecordsQuery: TeamCapexResourcesQuery,
    transformValues: transformCapexValues({ teamId }),
  });

  return (
    <SidebarEditContextProvider>
      <CapexesTable
        query={TeamCapexResourcesQuery}
        queryOptions={{ variables: { id: teamId } }}
        getQueryResult={(data) => getFirstQueryResult(data)?.capexResources}
        {...props}
      />
    </SidebarEditContextProvider>
  );
};

export const FarmCapexesTable = () => {
  const { farmId } = useParams();
  const props = useMutations({
    submitMutation: UpdateCapexesMutation,
    deleteMutation: DeleteCapexesMutation,
    fetchAllRecordsQuery: RawCapexesQuery,
    transformValues: transformCapexValues({ farmId }),
  });
  return (
    <SidebarEditContextProvider>
      <CapexesTable query={RawCapexesQuery} showFarmColumns {...props} />
    </SidebarEditContextProvider>
  );
};
